import { registerLocaleData } from '@angular/common';
import { NgModule, SecurityContext } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { LayoutModule } from './shared/layout/layout.module';
import { SharedModule } from './shared/shared.module';

import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { TitleStrategy } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { MarkdownModule } from 'ngx-markdown';
import { AppTitleStrategy } from './core/helpers/app-title-strategy';
import { ArrayTypeComponent } from './shared/layout/formly/array.type';
import { CheckboxTypeComponent } from './shared/layout/formly/checkbox.type';
import { ErrorFieldWrapperComponent } from './shared/layout/formly/error-field-wrapper';
import { FieldWrapperComponent } from './shared/layout/formly/field-wrapper';
import { InputTypeComponent } from './shared/layout/formly/input.type';
import { IntegerTypeComponent } from './shared/layout/formly/integer.type';
import { MinimalFieldWrapperComponent } from './shared/layout/formly/minimal-field-wrapper';
import { NumberTypeComponent } from './shared/layout/formly/number.type';
import { ObjectTypeComponent } from './shared/layout/formly/object.type';
import { TextareaTypeComponent } from './shared/layout/formly/textarea.type';

registerLocaleData(localeDe, localeDeExtra);

export function validationErrorMessage(error: unknown) {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Fehler';
}

export function minLengthValidationMessage(
  error: unknown,
  field: FormlyFieldConfig,
) {
  return `Die Eingabe darf nicht kürzer als ${field.props.minLength} Zeichen sein.`;
}

export function maxLengthValidationMessage(
  error: unknown,
  field: FormlyFieldConfig,
) {
  return `Die Eingabe darf nicht länger als ${field.props.maxLength} Zeichen sein.`;
}

export function minValidationMessage(
  error: unknown,
  field: FormlyFieldConfig,
) {
  return `Die Eingabe muss mindestens ${field.props.min} sein.`;
}

export function maxValidationMessage(
  error: unknown,
  field: FormlyFieldConfig,
) {
  return `Die Eingabe darf maximal ${field.props.max} sein.`;
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    ToastModule,
    CoreModule.forRoot(),
    LayoutModule,
    SharedModule,
    MarkdownModule.forRoot({
      sanitize: SecurityContext.STYLE,
    }),
    FormlyModule.forRoot({
      wrappers: [
        {
          name: 'formly-field-wrapper',
          component: FieldWrapperComponent,
        },
        {
          name: 'minimal-formly-field-wrapper',
          component: MinimalFieldWrapperComponent,
        },
        {
          name: 'error-formly-field-wrapper',
          component: ErrorFieldWrapperComponent,
        },
      ],
      validationMessages: [
        {
          name: 'required',
          message: $localize`Dieses Feld ist verpflichtend.`,
        },
        {
          name: 'pattern',
          message: $localize`Die Eingabe ist ungültig.`,
        },
        { name: 'minLength', message: minLengthValidationMessage },
        { name: 'maxLength', message: maxLengthValidationMessage },
        { name: 'min', message: minValidationMessage },
        { name: 'max', message: maxValidationMessage },
        { name: 'validation-error', message: validationErrorMessage },
      ],
      types: [
        {
          name: 'string',
          component: InputTypeComponent,
          wrappers: ['formly-field-wrapper'],
        },
        { name: 'object', component: ObjectTypeComponent },
        { name: 'array', component: ArrayTypeComponent },
        {
          name: 'integer',
          component: IntegerTypeComponent,
          wrappers: ['formly-field-wrapper'],
        },
        {
          name: 'boolean',
          component: CheckboxTypeComponent,
          wrappers: ['minimal-formly-field-wrapper'],
        },
        { name: 'enum', wrappers: ['error-formly-field-wrapper'] },
        { name: 'textarea', component: TextareaTypeComponent },
        {
          name: 'number',
          component: NumberTypeComponent,
          wrappers: ['formly-field-wrapper'],
        },
      ],
    }),
  ],
  providers: [
    MessageService,
    { provide: TitleStrategy, useClass: AppTitleStrategy },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
